import React, { useState, useEffect } from 'react';
import { normalizeData } from '../api';
import { EmptyState, InfoTooltip, ErrorState } from './shared/UIComponents';
import LoadingSpinner from './LoadingSpinner';

function DataNormalization({ datasetName }) {
  const [selectedDataset, setSelectedDataset] = useState(datasetName || '');
  const [normalizationMethod, setNormalizationMethod] = useState('zscore');
  const [normalizedData, setNormalizedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Update selectedDataset when datasetName prop changes
  useEffect(() => {
    if (datasetName) {
      setSelectedDataset(datasetName);
    }
  }, [datasetName]);

  const handleNormalizeData = async () => {
    if (!selectedDataset) {
      setError('Please select a dataset to normalize.');
      return;
    }
    setLoading(true);
    setError(null);
    setNormalizedData(null);
    try {
      const result = await normalizeData(selectedDataset, normalizationMethod);
      setNormalizedData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="data-normalization">
      <InfoTooltip 
        title="Data Normalization" 
        content="Scale your data to improve model performance. Normalization brings features to similar scales, making training more stable and faster."
      >
        <h2 className="data-normalization__title">Data Normalization</h2>
      </InfoTooltip>
      
      <div className="data-normalization__controls">
        <label className="data-normalization__label">Dataset: </label>
        <input 
          type="text" 
          value={selectedDataset} 
          onChange={e => setSelectedDataset(e.target.value)} 
          placeholder="Enter dataset name"
          className="data-normalization__input"
        />
        <label className="data-normalization__label">Method: </label>
        <select 
          value={normalizationMethod} 
          onChange={e => setNormalizationMethod(e.target.value)}
          className="data-normalization__select"
        >
          <option value="zscore">Z-Score</option>
          <option value="minmax">Min-Max</option>
          <option value="robust">Robust</option>
        </select>
        <button 
          onClick={handleNormalizeData} 
          disabled={loading || !selectedDataset}
          className="data-normalization__normalize-btn"
        >
          {loading ? 'Normalizing...' : 'Normalize Data'}
        </button>
      </div>
      
      {loading && (
        <div className="data-normalization__loading">
          <LoadingSpinner text={`Normalizing data using ${normalizationMethod} method...`} />
        </div>
      )}
      
      {error && (
        <ErrorState 
          message={error} 
          onRetry={() => {
            setError(null);
            handleNormalizeData();
          }}
        />
      )}
      
      {!selectedDataset && !loading && !error && !normalizedData && (
        <EmptyState 
          message="Enter a dataset name above to start normalizing your data."
          icon="📊"
        />
      )}
      
      {normalizedData && (
        <div className="data-normalization__results">
          <h3 className="data-normalization__results-title">Normalized Data Results</h3>
          <pre className="data-normalization__results-json">
            {JSON.stringify(normalizedData, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

export default DataNormalization;
